import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import LegislationFilters from './LegislationFilters';
import LegislationTable from './LegislationTable';
import LegislationCard from './LegislationCard';
import {
  Matter,
  FilterState,
  SortState,
  ViewState,
  FilterOptions,
  defaultFilterState,
  Committee,
} from '../../types/legislation';
import {
  searchMatters,
  fetchFilterOptions,
  getAvailableSessions,
  fetchTrackedCommittees,
} from '../../services/legislationApi';

const LegislationPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse initial state from URL
  const parseFiltersFromURL = useCallback((): FilterState => {
    return {
      search: searchParams.get('q') || '',
      searchBody: searchParams.get('searchBody') === 'true',
      searchAttachments: searchParams.get('searchAttachments') === 'true',
      statuses: searchParams.get('status')?.split(',').filter(Boolean) || [],
      types: searchParams.get('type')?.split(',').filter(Boolean) || [],
      sponsors: searchParams.get('sponsor')?.split(',').map(Number).filter(Boolean) || [],
      bodyId: searchParams.get('bodyId') ? Number(searchParams.get('bodyId')) : null,
      dateFrom: searchParams.get('from') || null,
      dateTo: searchParams.get('to') || null,
      dateField: (searchParams.get('dateField') as FilterState['dateField']) || 'intro',
      session: searchParams.get('session') || null,
    };
  }, [searchParams]);

  const parseSortFromURL = useCallback((): SortState => {
    return {
      field: (searchParams.get('sort') as SortState['field']) || 'MatterIntroDate',
      direction: (searchParams.get('dir') as SortState['direction']) || 'desc',
    };
  }, [searchParams]);

  const parsePageFromURL = useCallback((): number => {
    return parseInt(searchParams.get('page') || '0', 10);
  }, [searchParams]);

  const parseViewFromURL = useCallback((): ViewState => {
    return {
      mode: (searchParams.get('view') as ViewState['mode']) || 'table',
    };
  }, [searchParams]);

  // State
  const [filters, setFilters] = useState<FilterState>(parseFiltersFromURL);
  const [sort, setSort] = useState<SortState>(parseSortFromURL);
  const [page, setPage] = useState<number>(parsePageFromURL);
  const [view, setView] = useState<ViewState>(parseViewFromURL);
  const [results, setResults] = useState<Matter[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [committees, setCommittees] = useState<Committee[]>([]);
  const [searchInput, setSearchInput] = useState<string>(filters.search);

  const sessions = useMemo(() => getAvailableSessions(), []);
  const pageSize = 20;

  // Sync filters to URL
  const syncToURL = useCallback(
    (newFilters: FilterState, newSort: SortState, newView: ViewState, newPage: number) => {
      const params = new URLSearchParams();

      if (newFilters.search) params.set('q', newFilters.search);
      if (newFilters.searchBody) params.set('searchBody', 'true');
      if (newFilters.searchAttachments) params.set('searchAttachments', 'true');
      if (newFilters.statuses.length) params.set('status', newFilters.statuses.join(','));
      if (newFilters.types.length) params.set('type', newFilters.types.join(','));
      if (newFilters.sponsors.length) params.set('sponsor', newFilters.sponsors.join(','));
      if (newFilters.bodyId) params.set('bodyId', newFilters.bodyId.toString());
      if (newFilters.dateFrom) params.set('from', newFilters.dateFrom);
      if (newFilters.dateTo) params.set('to', newFilters.dateTo);
      if (newFilters.dateField !== 'intro') params.set('dateField', newFilters.dateField);
      if (newFilters.session) params.set('session', newFilters.session);
      if (newSort.field !== 'MatterIntroDate') params.set('sort', newSort.field);
      if (newSort.direction !== 'desc') params.set('dir', newSort.direction);
      if (newView.mode !== 'table') params.set('view', newView.mode);
      if (newPage > 0) params.set('page', newPage.toString());

      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  // Load filter options and committees on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [options, trackedCommittees] = await Promise.all([
          fetchFilterOptions(),
          fetchTrackedCommittees(),
        ]);
        setFilterOptions(options);
        setCommittees(trackedCommittees);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Monotonic id for the in-flight search. Searches fire from several sources
  // (debounced typing, filter toggles, sort, pagination) and resolve out of order;
  // without this guard a slower earlier response can overwrite a newer one, leaving
  // stale/over-inclusive results that only "fix" when you change a filter again.
  const searchSeqRef = useRef(0);

  // Search when filters/sort/page change
  useEffect(() => {
    const seq = ++searchSeqRef.current;
    const performSearch = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await searchMatters(filters, sort, page, pageSize);
        if (seq !== searchSeqRef.current) return; // superseded by a newer search
        // Defensive de-dup by MatterId so a bill never renders twice (also avoids
        // duplicate React keys) even if the API hands back a duplicate row.
        const seen = new Set<number>();
        const unique = response.items.filter((m) => {
          if (seen.has(m.MatterId)) return false;
          seen.add(m.MatterId);
          return true;
        });
        setResults(unique);
        setTotalCount(response.totalCount);
      } catch (err) {
        if (seq !== searchSeqRef.current) return;
        setError('Failed to search legislation. Please try again.');
        console.error(err);
      } finally {
        if (seq === searchSeqRef.current) setLoading(false);
      }
    };
    performSearch();
    syncToURL(filters, sort, view, page);
  }, [filters, sort, page, syncToURL, view]);

  // Debounced search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters((prev) => ({ ...prev, search: searchInput }));
        setPage(0);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput, filters.search]);

  // Filter handlers
  const handleFilterChange = useCallback((key: keyof FilterState, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  }, []);

  const handleToggleArrayFilter = useCallback((key: 'statuses' | 'types', value: string) => {
    setFilters((prev) => {
      const arr = prev[key];
      const newArr = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
      return { ...prev, [key]: newArr };
    });
    setPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(defaultFilterState);
    setSearchInput('');
    setPage(0);
  }, []);

  const handleRemoveFilter = useCallback((key: keyof FilterState, value?: string) => {
    setFilters((prev) => {
      if (key === 'statuses' || key === 'types') {
        return { ...prev, [key]: (prev[key] as string[]).filter((v) => v !== value) };
      }
      if (key === 'sponsors') {
        return { ...prev, [key]: prev[key].filter((v) => v !== Number(value)) };
      }
      return { ...prev, [key]: defaultFilterState[key] };
    });
    setPage(0);
  }, []);

  // Sort handler
  const handleSort = useCallback((field: SortState['field']) => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
    setPage(0);
  }, []);

  // View toggle
  const handleViewToggle = useCallback(() => {
    setView((prev) => ({ mode: prev.mode === 'table' ? 'card' : 'table' }));
  }, []);

  // Pagination
  const totalPages = Math.ceil(totalCount / pageSize);

  // Active filters for tags
  const activeFilters = useMemo(() => {
    const tags: Array<{ key: keyof FilterState; value: string; label: string }> = [];

    filters.statuses.forEach((s) => tags.push({ key: 'statuses', value: s, label: `Status: ${s}` }));
    filters.types.forEach((t) => tags.push({ key: 'types', value: t, label: `Type: ${t}` }));
    if (filters.bodyId) {
      const committee = committees.find(c => c.id === filters.bodyId);
      tags.push({ key: 'bodyId', value: filters.bodyId.toString(), label: `Committee: ${committee?.name || filters.bodyId}` });
    }
    filters.sponsors.forEach((sponsorId) => {
      const person = filterOptions?.persons?.find(p => p.PersonId === sponsorId);
      tags.push({ key: 'sponsors', value: sponsorId.toString(), label: `Sponsor: ${person?.PersonFullName || sponsorId}` });
    });
    if (filters.dateFrom) tags.push({ key: 'dateFrom', value: filters.dateFrom, label: `From: ${filters.dateFrom}` });
    if (filters.dateTo) tags.push({ key: 'dateTo', value: filters.dateTo, label: `To: ${filters.dateTo}` });
    if (filters.session) tags.push({ key: 'session', value: filters.session, label: `Session: ${filters.session}` });

    return tags;
  }, [filters, committees, filterOptions]);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Filter Panel */}
      <aside className="w-full lg:w-72 flex-shrink-0">
        <LegislationFilters
          filters={filters}
          filterOptions={filterOptions}
          sessions={sessions}
          committees={committees}
          onFilterChange={handleFilterChange}
          onToggleStatus={(status) => handleToggleArrayFilter('statuses', status)}
          onToggleType={(type) => handleToggleArrayFilter('types', type)}
          onClear={handleClearFilters}
        />
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Search Bar */}
        <div className="mb-4">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search legislation by title, name, or file number..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-nyc-blue focus:border-transparent text-lg"
          />
          <label className="inline-flex items-center mt-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.searchAttachments}
              onChange={(e) => handleFilterChange('searchAttachments', e.target.checked)}
              className="mr-2 rounded border-gray-300 text-nyc-blue focus:ring-nyc-blue"
            />
            Also search within attached documents (testimony, reports, etc.)
          </label>
        </div>

        {/* Active Filters */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {activeFilters.map((tag, idx) => (
              <span
                key={`${tag.key}-${tag.value}-${idx}`}
                className="inline-flex items-center px-3 py-1 bg-nyc-blue text-white text-sm rounded-full"
              >
                {tag.label}
                <button
                  onClick={() => handleRemoveFilter(tag.key, tag.value)}
                  className="ml-2 hover:text-nyc-orange"
                  aria-label={`Remove ${tag.label}`}
                >
                  &times;
                </button>
              </span>
            ))}
            <button onClick={handleClearFilters} className="text-sm text-gray-600 hover:text-nyc-blue underline">
              Clear all
            </button>
          </div>
        )}

        {/* Results Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-gray-600">
            {loading ? 'Searching...' : results.length >= pageSize ? `${pageSize}+ results` : `${results.length} result${results.length !== 1 ? 's' : ''}`}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleViewToggle}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
            >
              {view.mode === 'table' ? 'Card View' : 'Table View'}
            </button>
          </div>
        </div>

        {/* Results */}
        {loading && results.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nyc-blue mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading legislation...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600">{error}</p>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">No legislation found matching your criteria.</p>
          </div>
        ) : view.mode === 'table' ? (
          <LegislationTable items={results} sort={sort} onSort={handleSort} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {results.map((item) => (
              <LegislationCard key={item.MatterId} matter={item} />
            ))}
          </div>
        )}

        {/* Pagination - show if more than one page OR if we got a full page (likely more results) */}
        {(totalPages > 1 || results.length >= pageSize || page > 0) && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              Previous
            </button>
            <span className="text-gray-600">
              Page {page + 1}{totalCount > pageSize ? ` of ${totalPages}` : ''}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={results.length < pageSize}
              className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default LegislationPage;
